import { useModalStore } from '@/shared/lib';
import {
  TwoFactorAuthAppCodeModal,
  TwoFactorAuthAppEnable,
  TwoFactorIdentityDisableFormModal,
  TwoFactorIdentityEnableFormModal,
  TwoFactorSettingsModal,
} from '../ui';
import type {
  TwoFactorAppCodeModalArgs,
  TwoFactorAppEnableModalArgs,
  TwoFactorIdentityConfirmationDisableModalArgs,
  TwoFactorIdentityConfirmationEnableModalArgs,
  TwoFactorSettingsModalArgs,
} from './types';

const useTwoFactorSettingsModals = () => {
  const { showDialogComponent } = useModalStore();

  const openTwoFactorSettingsModal = ({
    onBack,
  }: Omit<TwoFactorSettingsModalArgs, 'goToCurrentScreen'>) => {
    showDialogComponent({
      component: (
        <TwoFactorSettingsModal
          onBack={onBack}
          goToCurrentScreen={() => openTwoFactorSettingsModal({ onBack })}
        />
      ),
      className: 'bg-secondary-bg',
    });
  };

  const openTwoFactorAppEnableModal = ({
    onBack,
    onSkip,
    onSuccess,
    onClose,
    preventOverlayClose,
    showCloseButton,
  }: Omit<TwoFactorAppEnableModalArgs, 'goToCurrentScreen'>) => {
    showDialogComponent({
      component: (
        <TwoFactorAuthAppEnable
          onBack={onBack}
          onSkip={onSkip}
          onSuccess={onSuccess}
          showCloseButton={showCloseButton}
          preventOverlayClose={preventOverlayClose}
          goToCurrentScreen={() => {
            openTwoFactorAppEnableModal({
              onBack,
              onSuccess,
              onSkip,
              preventOverlayClose,
              showCloseButton,
            });
          }}
        />
      ),
      showCloseButton,
      onClose,
      preventOverlayClose,
    });
  };

  const openTwoFactorAppCodeModal = ({
    code,
    onBack,
    uri,
    onSuccess,
    preventOverlayClose,
    showCloseButton,
  }: Omit<TwoFactorAppCodeModalArgs, 'goToCurrentScreen'>) => {
    showDialogComponent({
      component: (
        <TwoFactorAuthAppCodeModal
          code={code}
          uri={uri}
          onBack={onBack}
          onSuccess={onSuccess}
          showCloseButton={showCloseButton}
          preventOverlayClose={preventOverlayClose}
          goToCurrentScreen={() =>
            openTwoFactorAppCodeModal({
              onBack,
              code,
              uri,
              onSuccess,
              preventOverlayClose,
              showCloseButton,
            })
          }
        />
      ),
      showCloseButton,
      preventOverlayClose,
    });
  };

  const openTwoFactorIdentityEnableModal = ({
    identityType,
    identity,
    appKey,
    onBack,
    onSuccess,
    preventOverlayClose,
    showCloseButton,
  }: TwoFactorIdentityConfirmationEnableModalArgs) => {
    showDialogComponent({
      component: (
        <TwoFactorIdentityEnableFormModal
          identityType={identityType}
          onBack={onBack}
          appKey={appKey}
          identity={identity}
          onSuccess={onSuccess}
        />
      ),
      className: 'overflow-hidden',
      preventOverlayClose,
      showCloseButton,
    });
  };

  const openTwoFactorIdentityDisableModal = ({
    identityType,
    state,
    onBack,
    onSuccess,
  }: TwoFactorIdentityConfirmationDisableModalArgs) => {
    showDialogComponent({
      component: (
        <TwoFactorIdentityDisableFormModal
          identityType={identityType}
          state={state}
          onBack={onBack}
          onSuccess={onSuccess}
        />
      ),
      className: 'overflow-hidden',
    });
  };

  return {
    openTwoFactorSettingsModal,
    openTwoFactorAppEnableModal,
    openTwoFactorAppCodeModal,
    openTwoFactorIdentityEnableModal,
    openTwoFactorIdentityDisableModal,
  };
};

export { useTwoFactorSettingsModals };
